import { useEffect, useState } from "react";
import { Crown, Loader2 } from "lucide-react";
import Seo from "@/components/Seo";
import { api } from "@/lib/api";

const PROGRAM_LABEL = {
  men: "Men & Young Men",
  women: "Women & Young Girls",
};

export default function Fellowship() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/fellowship").then((r) => setData(r.data)).catch(() => setData({ fellows: [], count: 0 }));
  }, []);

  return (
    <div className="bg-tcpi-bg min-h-screen" data-testid="fellowship-page">
      <Seo
        title="The Fellowship — Crown Fellows of TCPI"
        description="An honor roll of every Crown Fellow — participants who completed all seven tracks of The Crown Path Initiative, a program of The 1123 Foundation."
        path="/fellowship"
      />
      <section className="max-w-5xl mx-auto px-6 md:px-12 py-20 md:py-24">
        <div className="text-center mb-14">
          <div className="overline mb-3">The Fellowship</div>
          <h1
            className="text-4xl md:text-6xl text-[#F7F5F0] leading-[1.05] mb-5"
            style={{ fontFamily: "Cormorant Garamond, serif" }}
          >
            Those who earned their crown.
          </h1>
          <p className="text-[#D1CFC9] max-w-2xl mx-auto leading-relaxed">
            Every name below is a participant who finished all seven tracks of The Crown Path
            Initiative — months of reflection, accountability, and choosing to do the work even
            when no one was watching.
          </p>
        </div>

        {!data && (
          <div className="text-center text-[#8A8782] flex items-center justify-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading the fellowship…
          </div>
        )}

        {data && data.count === 0 && (
          <div className="card-bento text-center py-16" data-testid="fellowship-empty">
            <Crown className="w-12 h-12 text-[#C9A961] mx-auto mb-4" strokeWidth={1.2} />
            <p className="text-[#D1CFC9] max-w-md mx-auto">
              The fellowship is just beginning. The next Crown Fellow will be the first to
              complete all seven tracks — and their name will appear here.
            </p>
          </div>
        )}

        {data && data.count > 0 && (
          <>
            <div className="text-center mb-10">
              <div className="inline-flex items-baseline gap-2 px-5 py-2 rounded-full border border-[#C9A961]/40 bg-[#C9A961]/5">
                <span
                  className="text-3xl text-[#C9A961]"
                  style={{ fontFamily: "Cormorant Garamond, serif" }}
                  data-testid="fellowship-count"
                >
                  {data.count}
                </span>
                <span className="text-sm text-[#D1CFC9] tracking-wide">
                  {data.count === 1 ? "Crown Fellow" : "Crown Fellows"} and growing
                </span>
              </div>
            </div>
            <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4">
              {data.fellows.map((f, i) => (
                <FellowCard key={`${f.display_name}-${i}`} fellow={f} />
              ))}
            </div>
          </>
        )}

        <div className="text-center mt-16 text-xs text-[#8A8782] italic">
          A program of <span className="text-[#C9A961]">The 1123 Foundation</span>.
        </div>
      </section>
    </div>
  );
}

function FellowCard({ fellow }) {
  let crownedDate = "";
  if (fellow.crowned_on) {
    try {
      crownedDate = new Date(fellow.crowned_on).toLocaleDateString(undefined, {
        month: "long",
        year: "numeric",
      });
    } catch {
      crownedDate = "";
    }
  }
  return (
    <div
      className="card-bento text-center hover:border-[#C9A961]/50 transition"
      data-testid="fellow-card"
    >
      <Crown className="w-7 h-7 text-[#C9A961] mx-auto mb-3" strokeWidth={1.4} />
      <div
        className="text-xl text-[#F7F5F0] mb-1"
        style={{ fontFamily: "Cormorant Garamond, serif" }}
        data-testid="fellow-name"
      >
        {fellow.display_name}
      </div>
      <div className="text-[10px] uppercase tracking-[0.16em] text-[#C9A961]/80">
        {PROGRAM_LABEL[fellow.program] || "Crown Path"}
      </div>
      {crownedDate && (
        <div className="text-xs text-[#8A8782] mt-2">Crowned {crownedDate}</div>
      )}
    </div>
  );
}
